import type { ComponentChildren, JSX } from 'preact';
import { useId, useRef } from 'preact/hooks';
import { X } from 'lucide-preact';
import { Portal } from './Portal';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useHotkey } from '@/hooks/useHotkey';
import { usePresence } from '@/hooks/usePresence';
import { IconButton } from '../primitives/Button';
import { cn } from '@/lib/cn';

export type DrawerSide = 'left' | 'right' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIDE_CONTAINER: Record<DrawerSide, string> = {
  left: 'justify-start',
  right: 'justify-end',
  bottom: 'items-end justify-stretch',
};

const SIDE_PANEL_BASE: Record<DrawerSide, string> = {
  left: 'h-full',
  right: 'h-full',
  bottom: 'w-full max-h-[85vh] rounded-t-xl',
};

const SIDE_PANEL_ENTER: Record<DrawerSide, string> = {
  left: 'animate-slide-in-left',
  right: 'animate-slide-in-right',
  bottom: 'animate-slide-in-bottom',
};

const SIDE_PANEL_EXIT: Record<DrawerSide, string> = {
  left: 'animate-slide-out-left',
  right: 'animate-slide-out-right',
  bottom: 'animate-slide-out-bottom',
};

const SIZE_LATERAL: Record<DrawerSize, string> = {
  sm: 'w-80',
  md: 'w-96',
  lg: 'w-[480px]',
  xl: 'w-[640px]',
  full: 'w-full',
};

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: ComponentChildren;
  description?: ComponentChildren;
  children?: ComponentChildren;
  footer?: ComponentChildren;
  closeOnOverlay?: boolean;
  closeOnEscape?: boolean;
  hideCloseButton?: boolean;
  className?: string;
  /** When true, the body wrapper has no padding — useful when the child is a full-bleed surface (e.g. Sidebar). */
  unstyledBody?: boolean;
}

export function Drawer({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  description,
  children,
  footer,
  closeOnOverlay = true,
  closeOnEscape = true,
  hideCloseButton = false,
  className,
  unstyledBody = false,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const { mounted, exiting, onExitEnd } = usePresence(open);

  useFocusTrap(panelRef, open);
  useBodyScrollLock(open);
  useHotkey('Escape', onClose, { enabled: open && closeOnEscape, allowInEditable: true });

  if (!mounted) return null;

  const handleAnimationEnd = (e: JSX.TargetedAnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    onExitEnd();
  };

  return (
    <Portal layer="drawer">
      <div
        className={cn(
          'fixed inset-0 flex',
          exiting ? 'animate-fade-out' : 'animate-fade-in',
          SIDE_CONTAINER[side],
        )}
        role="presentation"
      >
        <div
          className="duration-med absolute inset-0 bg-[var(--scrim)] transition-opacity"
          onClick={closeOnOverlay && !exiting ? onClose : undefined}
          aria-hidden="true"
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descId : undefined}
          onAnimationEnd={handleAnimationEnd}
          className={cn(
            'bg-surface border-border relative flex flex-col shadow-lg outline-none',
            side === 'bottom' ? 'border-t' : side === 'left' ? 'border-r' : 'border-l',
            SIDE_PANEL_BASE[side],
            exiting ? SIDE_PANEL_EXIT[side] : SIDE_PANEL_ENTER[side],
            side === 'bottom' ? '' : SIZE_LATERAL[size],
            className,
          )}
        >
          {(title || !hideCloseButton) && !unstyledBody && (
            <header className="border-border-subtle flex items-start justify-between gap-3 border-b px-5 py-4">
              <div className="flex flex-col gap-0.5">
                {title ? (
                  <h2
                    id={titleId}
                    className="text-fg-strong text-base font-semibold tracking-normal"
                  >
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <p id={descId} className="text-13 text-fg-muted">
                    {description}
                  </p>
                ) : null}
              </div>
              {!hideCloseButton ? (
                <IconButton icon={X} label="Cerrar" size="sm" onClick={onClose} />
              ) : null}
            </header>
          )}
          <div className={cn('flex-1 overflow-auto', unstyledBody ? 'flex flex-col' : 'px-5 py-5')}>
            {children}
          </div>
          {footer ? (
            <footer className="border-border-subtle bg-surface-2 flex items-center justify-end gap-2 border-t px-5 py-4">
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    </Portal>
  );
}
